import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const STAFF_SECTIONS = [
  { key: "tableau-de-bord", label: "Tableau de bord" },
  { key: "jeregul", label: "Jeregul" },
  { key: "laregul", label: "Planning" },
  { key: "corrections", label: "Rétroviseur" },
  { key: "tribus", label: "Tribus Pro" },
  { key: "bourse", label: "Bourse d'échange" },
  { key: "vehicules", label: "Véhicules" },
  { key: "conducteurs", label: "Conducteurs" },
  { key: "patients", label: "Patients & Clients" },
  { key: "export", label: "Export & Facturation" },
  { key: "statistiques", label: "Statistiques" },
  { key: "test", label: "Générateur de test" },
  { key: "abonnement", label: "Abonnement" },
  { key: "reglages", label: "Réglages" },
];

const EMPTY = { name: "", email: "", password: "", allowed_sections: [] };

export function StaffManager() {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditId(s.id); setForm({ name: s.name, email: s.email, password: "", allowed_sections: s.allowed_sections || [] }); setOpen(true); };

  const toggle = (key) => setForm((f) => ({
    ...f,
    allowed_sections: f.allowed_sections.includes(key) ? f.allowed_sections.filter((k) => k !== key) : [...f.allowed_sections, key],
  }));

  const save = async () => {
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/staff/${editId}`, { name: form.name, password: form.password || undefined, allowed_sections: form.allowed_sections });
        toast.success("Compte personnel mis à jour");
      } else {
        await api.post("/staff", form);
        toast.success("Compte personnel créé — communiquez-lui son email et son mot de passe");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le compte de ${s.name} ?`)) return;
    try { await api.delete(`/staff/${s.id}`); toast.success("Compte supprimé"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-3">
      <Button size="sm" onClick={openCreate} className="gap-2" data-testid="staff-add">
        <UserPlus className="h-4 w-4" /> Créer un compte personnel
      </Button>

      {staff.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Modules</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {staff.map((s) => (
              <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{s.email}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[10px]">{(s.allowed_sections || []).length} module(s)</Badge>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`staff-edit-${s.id}`}>
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(s)} data-testid={`staff-delete-${s.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {staff.length === 0 && <p className="text-xs text-muted-foreground">Aucun compte personnel pour l'instant.</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="staff-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifier le compte personnel" : "Nouveau compte personnel"}</DialogTitle>
            <DialogDescription className="text-xs">
              Ce compte accède à votre espace société, limité aux modules cochés.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="staff-name" />
            </div>
            <div>
              <Label className="text-xs">Email de connexion</Label>
              <Input type="email" value={form.email} disabled={!!editId} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="staff-email" />
            </div>
            <div>
              <Label className="text-xs">{editId ? "Nouveau mot de passe (laisser vide pour conserver)" : "Mot de passe (min. 6)"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="staff-password" />
            </div>
            <div>
              <Label className="text-xs">Modules autorisés</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {STAFF_SECTIONS.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.allowed_sections.includes(s.key)} onCheckedChange={() => toggle(s.key)} data-testid={`staff-section-${s.key}`} />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={busy || !form.name || (!editId && (!form.email || form.password.length < 6)) || form.allowed_sections.length === 0} data-testid="staff-save">
              {busy ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
